/**
 * markdown.ts — renders support messages, which are written in Markdown.
 *
 * Mirrors the server's email renderer (`Server/Source/Utilities/Markdown.ts`) so
 * a reply reads the same in the thread, in the composer's preview and in the
 * customer's inbox: GitHub-flavoured, single line breaks kept (plain-text
 * messages read unchanged), raw HTML escaped rather than interpreted, images
 * shown as links, and only `http:`, `https:` and `mailto:` link targets.
 *
 * The rendered HTML then goes through `sanitizeHTML` as well. The renderer
 * already refuses everything dangerous; the allow-list is the second lock, so a
 * future `marked` feature or a renderer mistake cannot reach the page.
 */

import { Marked, type Tokens } from 'marked';
import { sanitizeHTML } from '../sanitizeHTML';

/** Link schemes a rendered message may point at. */
const SAFE_SCHEMES = /^(https?:|mailto:)/i;

/** Escapes text for HTML content and attribute values. */
export function escapeMarkup(text: string): string {
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

/** Whether a link target is one a message may carry. */
function isSafeHref(href: string): boolean {
	return SAFE_SCHEMES.test(href.trim());
}

/** The configured renderer. One instance: `marked` keeps no state between parses. */
const markdown = new Marked({
	gfm: true,
	breaks: true,
	renderer: {
		/** Raw HTML, block or inline: shown as the text it is. */
		html({ text }: Tokens.HTML | Tokens.Tag): string {
			return escapeMarkup(text);
		},
		/** A link to a safe target, or just its text. */
		link({ href, title, tokens }: Tokens.Link): string {
			const text = this.parser.parseInline(tokens);
			/* An unsafe target keeps the words and loses the link. */
			if (!isSafeHref(href)) return text;
			const titleAttr = title ? ` title="${escapeMarkup(title)}"` : '';
			return `<a href="${escapeMarkup(href)}"${titleAttr}>${text}</a>`;
		},
		/** An image as a link to it — or as its alt text when the target is unsafe. */
		image({ href, text }: Tokens.Image): string {
			const label = escapeMarkup(text || href);
			/* Same rule as links: no unsafe target survives. */
			if (!isSafeHref(href)) return label;
			return `<a href="${escapeMarkup(href)}">${label}</a>`;
		}
	}
});

/**
 * Renders a support message's Markdown to HTML safe to insert with `{@html}`.
 *
 * @param text - The message as written.
 * @returns Sanitized HTML; links open in a new tab with `noopener noreferrer`.
 */
export function renderSupportMarkdown(text: string): string {
	return sanitizeHTML(markdown.parse(text, { async: false }) as string);
}
